import { useState } from "react";
import { Button } from "./Button";
import { useUIStore } from "../store/uiStore";
import { useConnectivityStore } from "../store/connectivityStore";
import { applyAppUpdate } from "../services/appUpdate";

const IOS_HINT_KEY = "ppg:ios-install-hint-dismissed";

function shouldShowIosInstallHint(): boolean {
  if (typeof window === "undefined") return false;
  const { userAgent } = navigator;
  const isIos =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);
  // In-app browsers and Chrome on iOS cannot add to the home screen.
  const isSafari =
    /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS/.test(userAgent);
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (!isIos || !isSafari || standalone) return false;
  try {
    return localStorage.getItem(IOS_HINT_KEY) == null;
  } catch {
    return false;
  }
}

function PromptCard({
  icon,
  message,
  actionLabel,
  onAction,
  onDismiss,
}: {
  icon: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <div
      role="status"
      className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-primary shadow-lg"
    >
      <span
        className="material-symbols-outlined text-xl text-secondary"
        aria-hidden="true"
      >
        {icon}
      </span>
      <p className="flex-1">{message}</p>
      {actionLabel && onAction && (
        <Button variant="primary" size="sm" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
      {onDismiss && (
        <Button
          variant="icon"
          size="sm"
          icon="close"
          aria-label="Dismiss"
          onClick={onDismiss}
        />
      )}
    </div>
  );
}

/**
 * App-wide, non-blocking prompts: a waiting app update, the connection
 * returning to a session that started offline, and the iOS install hint.
 */
export function AppPrompts() {
  const appUpdateReady = useUIStore((s) => s.appUpdateReady);
  const setAppUpdateReady = useUIStore((s) => s.setAppUpdateReady);
  const reconnectAvailable = useConnectivityStore((s) => s.reconnectAvailable);
  const [iosHint, setIosHint] = useState(shouldShowIosInstallHint);

  const dismissIosHint = () => {
    setIosHint(false);
    try {
      localStorage.setItem(IOS_HINT_KEY, "1");
    } catch {
      // Storage unavailable: the hint simply shows again next visit.
    }
  };

  if (!appUpdateReady && !reconnectAvailable && !iosHint) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
      {reconnectAvailable && (
        <PromptCard
          icon="cloud_done"
          message="Back online. Reload to sign in and edit."
          actionLabel="Reload"
          onAction={() => window.location.reload()}
        />
      )}
      {appUpdateReady && (
        <PromptCard
          icon="system_update"
          message="New version available."
          actionLabel="Reload"
          onAction={applyAppUpdate}
          onDismiss={() => setAppUpdateReady(false)}
        />
      )}
      {iosHint && !appUpdateReady && !reconnectAvailable && (
        <PromptCard
          icon="ios_share"
          message="Add PlotPlanGo to your Home Screen (Share → Add to Home Screen) to keep your trips available offline."
          onDismiss={dismissIosHint}
        />
      )}
    </div>
  );
}
